import React, { memo, useState } from "react";
import { Box, Button, Chip, Collapse, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { PartTag } from "./PartTag";
import { friendlyError } from "../utils/friendlyError";
import { fmtTime } from "../utils/historyText";

const FONT = "Inter, Poppins, sans-serif";
const NAVY = "#1E3A5F";

/** One error: a line with what happened (always), the message, what to do and the picture when opened. */
const ErrorRow = memo(({ log, number, parts, showPicture, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const f = friendlyError(log.errorMessage, { errorType: log.errorType, stage: log.stage, errorCode: log.errorCode });
  return (
    <Box data-testid="error-row" sx={{ mb: 1, border: "1px solid #FECDCA", backgroundColor: "#FEF3F2", borderRadius: "10px" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{ p: 1.25, cursor: "pointer", userSelect: "none" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <PartTag partKey={log.part} parts={parts} />
          <Chip label={`Try ${log.attemptNumber ?? number}`} size="small" sx={{ height: 20, fontSize: "11px", fontWeight: 600, color: "#B42318", backgroundColor: "#fff" }} />
          <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>{fmtTime(log.timestamp)}</Typography>
          <ExpandMoreRoundedIcon sx={{ ml: "auto", color: "#B42318", fontSize: 20, transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }} />
        </Box>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#912018", fontFamily: FONT, mt: 0.25 }}>{f.title}</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1.25 }}>
          {f.message && <Typography sx={{ fontSize: "12.5px", color: "#B42318", fontFamily: FONT }}>{f.message}</Typography>}
          {f.action && (
            <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, mt: 0.25 }}>
              <strong>What to do</strong> - {f.action}
            </Typography>
          )}
          {showPicture && log.screenshotUrl && (
            <Box
              component="img"
              src={log.screenshotUrl}
              loading="lazy"
              decoding="async"
              alt={`Try ${log.attemptNumber ?? number} screenshot`}
              onClick={() => window.open(log.screenshotUrl, "_blank", "noopener,noreferrer")}
              sx={{ display: "block", mt: 1, width: "100%", maxWidth: 520, maxHeight: 300, objectFit: "contain", objectPosition: "left top", borderRadius: "8px", border: "1px solid #EAECF0", cursor: "zoom-in", backgroundColor: "#fff" }}
            />
          )}
          {log.screenshotUrl && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.open(log.screenshotUrl, "_blank", "noopener,noreferrer")}
              sx={{ textTransform: "none", mt: 1, fontSize: "12px", borderRadius: "8px", color: NAVY, borderColor: "#C9D7E8", backgroundColor: "#fff" }}
            >
              View screenshot
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});
ErrorRow.displayName = "ErrorRow";

/**
 * Why runs stopped (2026-10-22): newest first; the newest is open, the others are one line each until clicked - a
 * picture is only downloaded once its error is opened (and lazily).
 */
const ErrorList = ({ logs = [], parts, showPicture = false }) => {
  if (!logs.length) return <Typography sx={{ fontSize: "12.5px", color: "#067647", fontFamily: FONT }}>No issues recorded.</Typography>;
  const newestFirst = [...logs].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  return (
    <Box>
      {newestFirst.map((log, i) => (
        <ErrorRow key={`${log.timestamp}-${i}`} log={log} number={newestFirst.length - i} parts={parts} showPicture={showPicture} defaultOpen={i === 0} />
      ))}
    </Box>
  );
};

export default memo(ErrorList);
